/**
 * WidgetPalette - Add widget toolbar
 *
 * Shows buttons to add new widgets to the screen. Uses the shared-types
 * factory functions to create new widgets with proper defaults.
 */

import { memo, useCallback } from 'react';
import {
  type AppWidgetType,
  createAppWidget,
  getWidgetDisplayName,
  getWidgetIcon,
} from '@deep-agents/shared-types';

interface WidgetPaletteProps {
  /** Current widget count (for setting order) */
  widgetCount: number;
  /** Called when a new widget should be added */
  onAddWidget: (type: AppWidgetType) => void;
}

// Widget types to show in the palette (in order)
const WIDGET_TYPES: AppWidgetType[] = ['card', 'text', 'button', 'list', 'slider', 'image'];

export const WidgetPalette = memo(function WidgetPalette({
  widgetCount,
  onAddWidget,
}: WidgetPaletteProps) {
  const handleAdd = useCallback(
    (type: AppWidgetType) => {
      onAddWidget(type);
    },
    [onAddWidget]
  );

  return (
    <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
      <span className="text-[10px] font-medium text-gray-500 mr-1">Add:</span>

      {WIDGET_TYPES.map((type) => (
        <button
          key={type}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleAdd(type);
          }}
          className="
            px-2 py-1 text-xs font-medium rounded-md
            bg-white hover:bg-violet-50 border border-gray-200 hover:border-violet-300
            text-gray-700 hover:text-violet-700
            transition-colors duration-150
            flex items-center gap-1
          "
          title={`Add ${getWidgetDisplayName(type)}`}
        >
          <span>{getWidgetIcon(type)}</span>
          <span className="hidden sm:inline">{getWidgetDisplayName(type)}</span>
        </button>
      ))}
    </div>
  );
});
